import * as bimdata from "@bimdata/bimdata-api-client";

const bimdataInstance = bimdata.ApiClient.instance;
// INFO - AM - 29/09/2022 - https://github.com/bimdata/javascript-api-client/blob/26fd4f06dc5c86a5e6b5b8476baa7b9f057edd59/src/ApiClient.js#L61
bimdataInstance.timeout = 20 * 60 * 1000;
bimdataInstance.basePath = import.meta.env.VITE_APP_BD_API_BASE_URL;
bimdataInstance.defaultHeaders = {};

let bcfApiInstance;
let collaborationApiInstance;
let ifcApiInstance;
let modelApiInstance;
let isInitialized = false;

// INFO - YS - 08/08/2025 - USE a promise to handle initialization and individual promises to provide APIs
// Create the promise immediately, to be resolved when initBimdataAuth is called
let initPromise;
let resolveInitPromise;

initPromise = new Promise((resolve) => {
  resolveInitPromise = resolve;
});

const createApiGetter = (getApiInstance, apiName) => {
  return async () => {
    if (!isInitialized) {
      console.warn(`⚠️ ${apiName} called before initBimdataAuth(). Waiting for initialization...`);
      await initPromise;
    }
    return getApiInstance();
  };
};

const getBcfApi = createApiGetter(() => bcfApiInstance, "bcfApi");
const getCollaborationApi = createApiGetter(() => collaborationApiInstance, "collaborationApi");
const getIfcApi = createApiGetter(() => ifcApiInstance, "ifcApi");
const getModelApi = createApiGetter(() => modelApiInstance, "modelApi");

const resetBimdata = () => {
  isInitialized = false;
  initPromise = new Promise((resolve) => {
    resolveInitPromise = resolve;
  });
  bcfApiInstance = null;
  collaborationApiInstance = null;
  ifcApiInstance = null;
  modelApiInstance = null;
};

const initBimdataAuth = (accessToken) => {
  if (isInitialized) return initPromise;

  const bimdata_connect = bimdataInstance.authentications["BIMData_Connect"];
  bimdata_connect.accessToken = accessToken;

  bcfApiInstance = new bimdata.BcfApi();
  collaborationApiInstance = new bimdata.CollaborationApi();
  ifcApiInstance = new bimdata.IfcApi();
  modelApiInstance = new bimdata.ModelApi();

  isInitialized = true;
  resolveInitPromise();

  return initPromise;
};
async function detectMimeType(file) {
  if (file.type) {
    return file.type;
  }
  const buffer = await file.slice(0, 8).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  if (hex.startsWith("89504e47")) return "image/png";
  if (hex.startsWith("ffd8ff")) return "image/jpeg";
  if (hex.startsWith("25504446")) return "application/pdf";
  if (hex.startsWith("47494638")) return "image/gif";
  if (hex.startsWith("504b0304")) return "application/zip";

  return "application/octet-stream"; // fallback
}

/**
 * INFO - VR - 24/12/2025 - RTEC-1533 - SuperAgent plugin for upload progress tracking
 * This plugin intercepts the request and attaches progress event listeners
 *
 * @param {Function} onProgress - Callback function (percent: number 0-100)
 * @returns {Function} SuperAgent plugin function
 */
const createProgressPlugin = (onProgress) => {
  return (request) => {
    if (onProgress) {
      request.on("progress", (event) => {
        // SuperAgent provides both upload and download progress
        // We're only interested in upload direction
        if (event.direction === "upload" && event.total > 0) {
          const percent = (event.loaded / event.total) * 100;
          onProgress(Math.min(percent, 100)); // Cap at 100%
        }
      });
    }
    return request;
  };
};

/**
 * INFO - VR - 24/12/2025 - RTEC-1533 - Upload file to BIMData GED with progress tracking
 *
 * @param {number} projectOriginId - BIMData project ID
 * @param {File} file - File to upload
 * @param {Function} onProgress - Optional progress callback (percent: number 0-100)
 * @returns {Promise<{id: number, mimeType: string, size: number}>} Upload result
 * @throws {Error} If client not initialized or upload fails
 */
const uploadFile = async (projectOriginId, file, onProgress) => {
  if (!collaborationApiInstance) {
    throw new Error("BIMData API client is not initialized");
  }
  const fileType = await detectMimeType(file);

  // INFO - VR - 24/12/2025 - Temporarily add progress plugin if callback provided
  const previousPlugins = bimdataInstance.plugins;
  if (onProgress) {
    bimdataInstance.plugins = [createProgressPlugin(onProgress)];
  }

  try {
    const response = await collaborationApiInstance.createDocument(
      import.meta.env.VITE_APP_CLOUD_ID,
      projectOriginId,
      file.name,
      file,
    );
    return {
      id: response.id,
      mimeType: fileType,
      size: file.size,
    };
  } catch (e) {
    console.error("Error uploading file to BIMData:", e);
    throw e;
  } finally {
    // INFO - VR - 24/12/2025 - Always restore previous plugins state
    bimdataInstance.plugins = previousPlugins;
  }
};

/**
 * INFO - YS - 23/03/2026 - RTEC-1808 - Refresh only the access token on an already-initialized client.
 * initBimdataAuth guards against double-init via isInitialized, so calling it again
 * after the 15-min interval would silently no-op and leave the expired token in place.
 */
const refreshBimdataToken = (accessToken) => {
  const bimdata_connect = bimdataInstance.authentications["BIMData_Connect"];
  bimdata_connect.accessToken = accessToken;
};

export {
  bimdataInstance,
  resetBimdata,
  initBimdataAuth,
  refreshBimdataToken,
  getBcfApi,
  getCollaborationApi,
  getIfcApi,
  getModelApi,
  uploadFile,
};
